import { Effect, type Scope } from 'effect';
import { attemptApi, attemptApiPromise, type ApiRuntimeError } from './effect-runtime';

/** Keep the request signal and unread body owned until the consumer finishes. */
export const withApiResponseEffect = Effect.fn('web.api.response')(function* <A, E>(
  url: string,
  init: RequestInit,
  use: (response: Response) => Effect.Effect<A, E>,
): Effect.fn.Return<A, ApiRuntimeError | E, Scope.Scope> {
  const owner = yield* Effect.acquireRelease(
    Effect.sync(() => ({
      controller: new AbortController(),
      response: undefined as Response | undefined,
    })),
    (owner) =>
      Effect.gen(function* () {
        owner.controller.abort();
        const body = owner.response?.body;
        if (body && !body.locked) {
          yield* attemptApiPromise('fetch', 'response-discard', () => body.cancel()).pipe(
            Effect.ignore,
          );
        }
      }),
  );
  const signal = init.signal
    ? AbortSignal.any([owner.controller.signal, init.signal])
    : owner.controller.signal;
  const response = yield* attemptApiPromise('fetch', 'http-request', async () => {
    const response = await fetch(url, { ...init, signal });
    // Some transports ignore abort. Discard late ownership instead of adopting it.
    if (signal.aborted) {
      await response.body?.cancel().catch(() => undefined);
      throw new Error('Response arrived after cancellation');
    }
    owner.response = response;
    return response;
  });
  return yield* use(response);
}, Effect.scoped);

export const readApiResponseBytesEffect = Effect.fn('web.api.readBytes')(function* (
  response: Response,
): Effect.fn.Return<Uint8Array, ApiRuntimeError, Scope.Scope> {
  const reader = response.body
    ? yield* Effect.acquireRelease(
        attemptApi('parse', 'response-reader', () => response.body!.getReader()),
        (reader) =>
          attemptApiPromise('fetch', 'response-reader-cancel', () => reader.cancel()).pipe(
            Effect.ignore,
            Effect.ensuring(
              attemptApi('parse', 'response-reader-release', () => reader.releaseLock()).pipe(
                Effect.ignore,
              ),
            ),
          ),
      )
    : undefined;
  const chunks: Uint8Array[] = [];
  let length = 0;
  if (reader) {
    while (true) {
      const chunk = yield* attemptApiPromise('parse', 'response-read', () => reader.read());
      if (chunk.done) break;
      chunks.push(chunk.value);
      length += chunk.value.length;
    }
  }
  return yield* attemptApi('parse', 'response-buffer', () => {
    const bytes = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.length;
    }
    return bytes;
  });
}, Effect.scoped);
